import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, IconButton,
  Tabs, Tab, CircularProgress, Alert, Divider
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import ConfirmationDialog from './confirmationDialog';
import { Memory } from '../../types';
import Markdown from 'react-markdown';
import { getStorageItems, deleteStorageItem } from '../../services/api';
import { useChat } from '../../context/chatContext';

interface ManageMemoriesProps {
  // The component now only needs the ID to fetch memories for.
  sessionId?: string;
  datasetName?: string;
}
const ManageMemories: React.FC<ManageMemoriesProps> = ({ sessionId, datasetName }) => {
  const [tab, setTab] = useState(0);
  const [impressions, setImpressions] = useState<Memory[]>([]);
  const [events, setEvents] = useState<Memory[]>([]);
  const [schemas, setSchemas] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'event' | 'impression' | 'schema' } | null>(null);
  const [hasMoreSchemas, setHasMoreSchemas] = useState<boolean>(true)
  const [hasMoreEvents, setHasMoreEvents] = useState<boolean>(true)
  const [hasMoreImpressions, setHasMoreImpressions] = useState<boolean>(true)
  const { setSessionId, setToken } = useChat()


  const fetchData = useCallback(async () => {
    if (!hasMoreImpressions && !hasMoreSchemas) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [eventData, impressionsData, schemasData] = await Promise.all([
        getStorageItems('events', sessionId, datasetName),
        getStorageItems('impressions', sessionId, datasetName),
        getStorageItems('schemas', sessionId, datasetName),
      ]);

      hasMoreEvents && setEvents(eventData.items.map((item: any) => ({ id: item.id, summary: item.content })));
      hasMoreImpressions && setImpressions(impressionsData.items.map((item: any) => ({ id: item.id, summary: item.content })));
      hasMoreSchemas && setSchemas(schemasData.items.map((item: any) => ({ id: item.id, summary: item.description })));
      setHasMoreEvents(eventData.has_more);
      setHasMoreImpressions(impressionsData.has_more);
      setHasMoreSchemas(schemasData.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [sessionId, datasetName, hasMoreEvents, hasMoreImpressions, hasMoreSchemas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (id: string, type: 'event' | 'impression' | 'schema') => setItemToDelete({ id, type });

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setLoading(true);
    try {
      const { id, type } = itemToDelete;

      // The user ID or session ID is required by the backend endpoint
      const userIdentifier = sessionId; // Fallback if needed

      if(!userIdentifier) {
        setError('Session was not found')
      }

      // FIX: Change to a POST request with a JSON body
      await deleteStorageItem(userIdentifier!, id, type, datasetName, setSessionId, setToken);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : `An unknown error occurred during deletion`);
    } finally {
      setItemToDelete(null);
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Tabs variant="fullWidth" value={tab} onChange={(e, newValue) => setTab(newValue)} centered>
          <Tab label={`Events (${events.length})`} />
          <Tab label={`Impressions (${impressions.length})`} />
          <Tab label={`Schemas (${schemas.length})`} />
        </Tabs>
        <Divider />
        <Box sx={{ mt: 2, maxHeight: '45vh', overflowY: 'auto' }}>
          {tab === 0 && (
            <List>
              {events.map((item) => (
                <ListItem key={item.id} divider secondaryAction={
                  <IconButton edge="end" onClick={() => handleDeleteClick(item.id, 'event')}><Delete /></IconButton>
                }>
                  <ListItemText primary={(
                    <Markdown>
                        {item.summary}
                    </Markdown>
                  )} secondary={`ID: ${item.id}`} />
                </ListItem>
              ))}
            </List>
          )}
          {tab === 1 && (
            <List>
              {impressions.map((item) => (
                <ListItem key={item.id} divider secondaryAction={
                  <IconButton edge="end" onClick={() => handleDeleteClick(item.id, 'impression')}><Delete /></IconButton>
                }>
                  <ListItemText primary={<Markdown>
                        {item.summary}
                    </Markdown>} secondary={`ID: ${item.id}`} />
                </ListItem>
              ))}
            </List>
          )}
          {tab === 2 && (
            <List>
              {schemas.map((item) => (
                <ListItem key={item.id} divider secondaryAction={
                  <IconButton edge="end" onClick={() => handleDeleteClick(item.id, 'schema')}><Delete /></IconButton>
                }>
                  <ListItemText primary={<Markdown>
                        {item.summary}
                    </Markdown>} secondary={`ID: ${item.id}`} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Memory"
        description="Are you sure you want to delete this memory? This action cannot be undone."
      />
    </>
  );
};

export default ManageMemories;